import { useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import { object, ref, string } from 'yup';
import { Backdrop, DialogConfirmation } from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { useProperty } from '@spotify-internal/remote-config-integration-react';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { parseURI, URITypeMap } from '@spotify-internal/uri';
import { useEnrichard } from '@hooks/useEnrichard';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { contributorCreditsRollout } from '@lib/remoteConfig/properties';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { albumUriValidationRegex } from '@helpers/regexPatterns';
import { ContributorCreditsForm } from './ContributorCreditsForm';
import { FooterButtons } from './FooterButtons';
import { FormBody } from './FormBody';
import {
  buildPage1Data,
  getAlbumUris,
  getArtistUri,
  getCaseValue,
  getURI,
  Page1Data,
  validComment,
} from './helpers';
import { SuccessState } from './SuccessState';
import { FORM_CASES, FormValues } from './types';

const StyledDialogConfirmation = styled(DialogConfirmation)`
  min-width: 1000px;
`;

interface ArtistRelocationModalProps {
  firstBtnRef: React.MutableRefObject<HTMLElement | null>;
  lastBtnRef: React.MutableRefObject<HTMLElement | null>;
  onClose: () => void;
  uri: string;
  additionalComment?: string;
}

// Elements a user can Tab to. Keep in sync with useModalPopoverFocus's Tab
// trap, which wraps focus between whatever these two refs point at.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export const ArtistRelocationModal = ({
  firstBtnRef,
  lastBtnRef,
  onClose,
  uri,
  additionalComment,
}: ArtistRelocationModalProps) => {
  const t = useT();
  const [page, setPage] = useState<1 | 2>(1);
  const [page2Submitted, setPage2Submitted] = useState(false);
  const [page1Data, setPage1Data] = useState<Page1Data | null>(null);
  // Page 1 is rendered by a Formik instance that's fully unmounted (not just
  // hidden) while on page 2 — see the early-return below — so its internal
  // state doesn't survive a page2 -> page1 round trip on its own. Persist the
  // raw values here so Formik can be re-seeded with them if the user comes
  // back from page 2 instead of losing everything they typed.
  const [page1FormValues, setPage1FormValues] = useState<FormValues>(() => ({
    email: '',
    albumURIs: uri,
    artistName: '',
    comments: '',
    case: FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
    destinationArtistURI: '',
    incorrectArtistURI: '',
    myArtistURI: '',
    currentArtistURI: '',
  }));
  const isContributorCreditsEnabled = useProperty(contributorCreditsRollout);
  const page2DialogRef = useRef<HTMLDivElement | null>(null);
  const {
    actions: { postToEnrichard, clearFieldError },
    status,
    errors: apiErrors,
  } = useEnrichard();
  const isLoading = status === FETCH_STATUS.FETCHING;
  const isFetched = status === FETCH_STATUS.SUCCESS;

  const handleSubmit = async (formValues: FormValues) => {
    const caseType = formValues.case;

    sendEvent({
      eventCategory: EVENT_CATEGORY.CONTENT_RELOCATION_FORM,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Content relocation form submitted',
      product: GA_PRODUCT_CATALOG,
    });
    const caseEnumValue = getCaseValue(caseType);
    const destinationArtist =
      formValues.case === FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE
        ? getURI(formValues.incorrectArtistURI)
        : '';

    const albumUris = getAlbumUris(formValues.albumURIs);
    const artistUri = getArtistUri(caseType, formValues);
    const comments = additionalComment
      ? `${additionalComment}\n----\n${formValues.comments}`
      : formValues.comments;

    const body = JSON.stringify({
      email: formValues.email,
      problem: caseEnumValue,
      date: new Date().toUTCString(),
      artist_uri: artistUri,
      other_artist_uri: destinationArtist,
      release_uris: albumUris,
      artist_name:
        caseType === FORM_CASES.MOVE_MY_RELEASE ? formValues.artistName : null,
      comments: comments,
    });

    await postToEnrichard(body);
  };

  const goToPage2 = (values: FormValues) => {
    setPage1FormValues(values);
    setPage1Data(buildPage1Data(values, additionalComment));
    setPage(2);
  };

  // Page 2's body is rendered by content-platform-ui's <Form> (async-loaded,
  // and its field set varies per flow config) rather than the hand-written
  // markup FormBody/FooterButtons use on page 1, so we can't wire firstBtnRef
  // / lastBtnRef onto specific known elements ahead of time. Instead, scan
  // the rendered dialog (body + footer) for whatever is actually focusable
  // and keep the modal's Tab trap (useModalPopoverFocus) pointed at the real
  // first/last elements, re-scanning whenever that subtree changes (loading
  // -> form, validation errors appearing, or the success state).
  useEffect(() => {
    if (!(isContributorCreditsEnabled && page === 2)) {
      return undefined;
    }

    const container = page2DialogRef.current;
    if (!container) {
      return undefined;
    }

    // Tracks whether we've already moved focus for this page/page2Submitted
    // transition (the effect's deps), reset each time the effect re-runs.
    // Without this, every DOM mutation inside the container (e.g. an inline
    // validation error appearing as the user types) would steal focus back.
    let hasFocusedForTransition = false;

    const updateFocusRefs = () => {
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      firstBtnRef.current = focusable[0] ?? null;
      lastBtnRef.current = focusable[focusable.length - 1] ?? null;

      // The element that was focused pre-transition (e.g. page 1's Next
      // button, or page 2's Submit) is now unmounted, so focus has silently
      // fallen back to document.body — move it into the dialog once so the
      // Tab trap (which only wraps focus already inside the boundary refs)
      // keeps working. Fall back to the dialog container itself on the off
      // chance nothing focusable has rendered yet.
      if (!hasFocusedForTransition) {
        (firstBtnRef.current ?? container).focus();
        hasFocusedForTransition = true;
      }
    };

    updateFocusRefs();

    const observer = new MutationObserver(updateFocusRefs);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'tabindex'],
    });

    return () => observer.disconnect();
  }, [
    isContributorCreditsEnabled,
    page,
    page2Submitted,
    firstBtnRef,
    lastBtnRef,
  ]);

  const validArtistURIMessage = t(
    'atlas.relocation.validation.artist_uri',
    `Please enter a valid Spotify Artist URI/URL. 'spotify:artist:uri-id'`,
    'This message appears when the user uses an invalid Spotify artist URI/URL',
  );
  const sameSourceAndDestinationArtistURIErrorMessage = t(
    'atlas.relocation.validation.source_and_destination_artist_uri',
    'Source and Destination Artist URI/URLs should not be the same',
    'This message appears when the source and destination artist URI/URL are the same',
  );

  const correctArtistURIErrorMessage = t(
    'atlas.relocation.required.correct_artist_URI',
    'Please enter the Correct Artist URI/URL to where your music should be relocated',
    'This message appears when the user leaves the correct artist URI/URL field blank',
  );

  const validationSchema = object({
    email: string()
      .email(
        t(
          'atlas.relocation.validation.valid_email',
          'A valid email address is required',
          'This message appears when the user uses an invalid email address',
        ),
      )
      .required(
        t(
          'atlas.relocation.required.valid_email',
          'Email address is required',
          'This message appears when the user leaves the email address field blank',
        ),
      ),
    case: string()
      .oneOf([
        FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
        FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE,
        FORM_CASES.MOVE_MY_RELEASE,
      ])
      .required(
        t(
          'atlas.relocation.required.select_one_option',
          'Please select one of the available options.',
          'This message appears when the user does not select a case',
        ),
      ),
    destinationArtistURI: string().when('case', {
      is: FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
      then: string()
        .required(correctArtistURIErrorMessage)
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        )
        .notOneOf(
          [ref('incorrectArtistURI')],
          sameSourceAndDestinationArtistURIErrorMessage,
        ),
      otherwise: string().notRequired(),
    }),
    incorrectArtistURI: string().when('case', {
      is: FORM_CASES.MY_RELEASE_ON_ANOTHER_PAGE,
      then: string()
        .required(
          t(
            'atlas.relocation.required.incorrect_artist_URI',
            'Please enter the Incorrect Artist URI/URL which your music is currently located at',
            'This message appears when the user leaves the incorrect artist URI/URL field empty',
          ),
        )
        .notOneOf(
          [ref('destinationArtistURI')],
          sameSourceAndDestinationArtistURIErrorMessage,
        )
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        ),
    }),
    myArtistURI: string().when('case', {
      is: FORM_CASES.ANOTHER_ARTIST_ON_MY_PAGE,
      then: string()
        .required(correctArtistURIErrorMessage)
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        ),
    }),
    currentArtistURI: string().when('case', {
      is: FORM_CASES.MOVE_MY_RELEASE,
      then: string()
        .required(correctArtistURIErrorMessage)
        .test(
          'isArtistUri',
          validArtistURIMessage,
          value => parseURI(value)?.type === URITypeMap.ARTIST,
        ),
    }),
    artistName: string().when('case', {
      is: FORM_CASES.MOVE_MY_RELEASE,
      then: string().required(
        t(
          'atlas.relocation.required.artist_name',
          'Please enter the name of the new Artist Page to where your music should be relocated',
          'This message appears when the user leaves the artist name field empty',
        ),
      ),
    }),
    albumURIs: string()
      .required(
        t(
          'atlas.relocation.required.album_uris',
          'Please enter the Spotify Album URI/URL of the affected album',
          'This message appears when the user leaves the album URI/URLs field empty',
        ),
      )
      .matches(
        albumUriValidationRegex,
        t(
          'atlas.relocation.validation.album_uri',
          'Please enter a valid Spotify Album URI/URL. For multiple Spotify Album URI/URL submissions, please separate each with a comma, space or semicolon',
          'This message appears when the Spotify album URI/URL (singular) is invalid',
        ),
      )
      .test(
        'isAlbumUri',
        t(
          'atlas.relocation.validation.album_uris',
          'Please enter valid Spotify Album URI/URLs. For multiple Spotify Album URI/URL submissions, please separate each with a comma, space or semicolon',
          'This message appears when the Spotify album URI/URLs (plural) is invalid',
        ),
        value =>
          value
            ?.split(/,| |\n|;/)
            .map(album => album.trim())
            .filter(album => album.length > 0)
            .filter(album => parseURI(album)?.type === URITypeMap.ALBUM)
            .length ===
          value?.split(/,| |;/).filter(album => album.length > 0).length,
      ),
    comments: string()
      .notRequired()
      .max(
        150,
        t(
          'atlas.relocation.validation.exceeds_150_chars',
          'Please enter no more than 150 characters',
          'This message appears when the text input exceeds 150 characters',
        ),
      )
      .test(
        'noUrisInValue',
        t(
          'atlas.relocation.validation.no_uris',
          'Please do not enter URI/URLs here',
          'This message appears if URI/URLs appear in this field',
        ),
        comments => validComment(comments),
      ),
  });

  // Page 2: render outside Formik to avoid nested <form> elements
  if (isContributorCreditsEnabled && page === 2) {
    const page2Body = page2Submitted ? (
      <SuccessState
        title={t(
          'atlas.success.form_submitted',
          'Form submitted',
          'Confirmation that form was succesfully submitted',
        )}
        text={t(
          'atlas.success.thanks_for_reaching_out_watch_inbox',
          'Thanks for reaching out. Watch your inbox for a response from us.',
          'Indicates to user that we will reach out to them via email',
        )}
      />
    ) : (
      <ContributorCreditsForm
        onSuccess={() => setPage2Submitted(true)}
        destinationArtistUri={page1Data?.destinationArtistUri ?? ''}
        incorrectArtistUri={page1Data?.incorrectArtistUri ?? ''}
        myArtistUri={page1Data?.myArtistUri ?? ''}
        currentArtistUri={page1Data?.currentArtistUri ?? ''}
        releaseUris={page1Data?.releaseUris ?? []}
        artistName={page1Data?.artistName ?? ''}
        problem={page1Data?.problem ?? ''}
        comments={page1Data?.comments ?? ''}
        date={page1Data?.date ?? ''}
        email={page1Data?.email ?? ''}
      />
    );

    return (
      <Backdrop center onClose={onClose}>
        <div ref={page2DialogRef} tabIndex={-1}>
          <StyledDialogConfirmation
            id="artist-relocation-modal"
            dialogTitle={t(
              'atlas.relocation.content_relocation_form',
              'Content Relocation Form',
              'Title for form to relocate content',
            )}
            body={page2Body}
            footer={
              <FooterButtons
                lastBtnRef={lastBtnRef}
                isFetched={page2Submitted}
                submitButtonDisabled={false}
                onClose={onClose}
                page={2}
                onBack={() => setPage(1)}
              />
            }
          />
        </div>
      </Backdrop>
    );
  }

  // Page 1: existing Formik form
  return (
    <Backdrop center onClose={onClose}>
      <Formik
        initialValues={page1FormValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          // Formik's <Form> submits natively on Enter in a text input, same
          // as clicking a type="submit" button — page 1 has neither (Next is
          // type="button"), so this is the only path a keyboard-only Enter
          // submission takes. Route it the same place Next goes, otherwise
          // it silently falls through to the legacy submission below and
          // bypasses contributor credits entirely.
          if (isContributorCreditsEnabled) {
            goToPage2(values);
            return undefined;
          }
          return handleSubmit(values);
        }}
      >
        {({
          values,
          handleChange,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          validateForm,
          setTouched,
        }) => {
          const handleNext = async () => {
            const validationErrors = await validateForm();
            if (Object.keys(validationErrors).length === 0) {
              goToPage2(values);
            } else {
              // Touch all erroring fields to surface inline error messages
              const touchedFields = Object.keys(validationErrors).reduce<
                Record<string, boolean>
              >((acc, key) => ({ ...acc, [key]: true }), {});
              setTouched(touchedFields);
            }
          };

          return (
            <Form>
              <StyledDialogConfirmation
                id="artist-relocation-modal"
                dialogTitle={t(
                  'atlas.relocation.content_relocation_form',
                  'Content Relocation Form',
                  'Title for form to relocate content',
                )}
                body={
                  <FormBody
                    firstBtnRef={firstBtnRef}
                    status={status}
                    apiErrors={apiErrors}
                    clearFieldError={clearFieldError}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                  />
                }
                footer={
                  <FooterButtons
                    lastBtnRef={lastBtnRef}
                    isFetched={isFetched}
                    submitButtonDisabled={isSubmitting || isLoading}
                    onClose={onClose}
                    page={1}
                    onNext={handleNext}
                    enablePage2={isContributorCreditsEnabled}
                  />
                }
              />
            </Form>
          );
        }}
      </Formik>
    </Backdrop>
  );
};
